// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { getAccountTransactions, formatAmount, buildEvent } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { useNavigate } from 'react-router-dom';
import {
  calculateCostBasis,
  calculateTotalCostBasis,
  calculateSharesHeld,
  calculateMarketValue,
  calculateUnrealizedGain,
  getLatestPrice,
  getCommodityId,
  getInvestmentAction,
  formatShares,
  SHARE,
} from '../../lib/investment.js';
import { InvestmentTransactionForm } from './InvestmentTransactionForm.js';
import type { Account, TransactionDeletedPayload } from '../../types.js';

interface InvestmentRegisterProps {
  account: Account;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

// Action codes that have a short display label (translated via
// investment:register.actionLabels.*). Others render blank.
const LABELED_ACTIONS = new Set(['buy', 'sell', 'div', 'rdiv', 'split']);

const ACTION_COLORS: Record<string, string> = {
  buy: 'text-accent',
  sell: 'text-text-muted',
  div: 'text-positive',
  rdiv: 'text-positive',
  split: 'text-warning',
  other: 'text-text-subtle',
};

export function InvestmentRegister({ account, worker, keyData }: InvestmentRegisterProps) {
  const { t } = useTranslation(['investment', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [showForm, setShowForm] = useState(false);
  const [sortDesc, setSortDesc] = useState(false);
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);

  const allTransactions = useMemo(
    () => getAccountTransactions(ledger, account.id),
    [ledger, account.id],
  );

  const displayTransactions = sortDesc ? [...allTransactions].reverse() : allTransactions;

  const commodityId = getCommodityId(account);
  const allPrices = [...ledger.prices.values()];
  const today = new Date().toISOString().slice(0, 10);
  const latestPrice = getLatestPrice(commodityId, today, allPrices);

  const sharesHeld = useMemo(
    () => calculateSharesHeld(account.id, allTransactions),
    [allTransactions, account.id],
  );

  const costBasisLots = useMemo(
    () => calculateCostBasis(account.id, allTransactions),
    [allTransactions, account.id],
  );
  const totalCostBasis = calculateTotalCostBasis(costBasisLots);

  const marketValue = latestPrice
    ? calculateMarketValue(sharesHeld, latestPrice.value)
    : null;
  const unrealized = marketValue !== null
    ? calculateUnrealizedGain(marketValue, totalCostBasis)
    : null;

  // Running share balance per transaction
  const withShareBalance = useMemo(() => {
    let running = 0;
    return allTransactions.map((tx) => {
      const split = tx.splits.find((s) => s.accountId === account.id);
      running += split?.quantity ?? 0;
      return { tx, shareBalance: running };
    });
  }, [allTransactions, account.id]);
  const shareBalanceMap = new Map(withShareBalance.map(({ tx, shareBalance }) => [tx.id, shareBalance]));

  async function handleDelete(txId: string) {
    if (!confirm(t('register.confirmDelete'))) return;
    try {
      const payload: TransactionDeletedPayload = { transactionId: txId };
      await encryptAndPush(await buildEvent('TransactionDeleted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('register.deleteFailed'));
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-bg-surface shrink-0">
        <div className="flex items-baseline gap-3">
          <h2 className="text-md font-semibold text-text">{account.name}</h2>
          {account.commodity && (
            <span className="text-xs font-mono text-text-muted">{account.commodity.ticker}</span>
          )}
          <span className="text-sm font-mono text-text-muted">
            {formatShares(sharesHeld)} {t('register.sharesUnit')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSortDesc(!sortDesc)}
            className="px-2 py-1 text-xs border border-border text-text-muted hover:bg-bg-elevated hover:text-text transition-colors"
          >
            {sortDesc ? `↑ ${t('register.oldest')}` : `↓ ${t('register.newest')}`}
          </button>
          <button
            onClick={() => navigate(`/settings/commodities/${encodeURIComponent(commodityId)}/prices`)}
            className="px-3 py-1 border border-border text-text-muted text-sm hover:bg-bg-elevated hover:text-text transition-colors"
          >
            {t('register.prices')}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-1 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            {t('common:actions.new')}
          </button>
        </div>
      </div>

      {/* Register table */}
      <div className="flex-1 overflow-auto">
        {displayTransactions.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-text-muted">
            <div className="text-center">
              <p className="text-sm">{t('register.noTransactions')}</p>
              <button onClick={() => setShowForm(true)} className="mt-1 text-accent hover:text-accent-hover text-sm">
                {t('register.addFirst')}
              </button>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="bg-bg-surface border-b border-border-strong sticky top-0 z-10">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{t('register.columns.date')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 50 }}>{t('register.columns.action')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2">{t('register.columns.description')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{t('register.columns.shares')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 90 }}>{t('register.columns.price')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2" style={{ width: 100 }}>{t('register.columns.value')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-3" style={{ width: 90 }}>{t('register.columns.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {displayTransactions.map((tx, idx) => {
                const split = tx.splits.find((s) => s.accountId === account.id);
                const action = getInvestmentAction(tx, account.id);
                const shares = split?.quantity ?? 0;
                const value = split?.amount ?? 0;
                const shareBalance = shareBalanceMap.get(tx.id) ?? 0;
                const isExpanded = expandedTxId === tx.id;
                const rowBg = idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface';

                // Price per share for display (if we have shares and value)
                const pricePerShare = shares !== 0 ? Math.abs(Math.round((value * SHARE) / shares)) : null;

                return (
                  <React.Fragment key={tx.id}>
                    <tr
                      className={`border-b border-border cursor-pointer hover:bg-bg-elevated transition-colors ${rowBg} ${tx.voided ? 'opacity-40' : ''}`}
                      style={{ height: 28 }}
                      onClick={() => setExpandedTxId(isExpanded ? null : tx.id)}
                    >
                      <td className="py-0.5 px-2 text-text-muted font-mono text-xs whitespace-nowrap">{formatDate(tx.date, { month: '2-digit', day: '2-digit', year: '2-digit' })}</td>
                      <td className={`py-0.5 px-2 text-xs font-medium ${ACTION_COLORS[action] ?? 'text-text-subtle'}`}>
                        {LABELED_ACTIONS.has(action) ? t(`register.actionLabels.${action}`) : ''}
                      </td>
                      <td className="py-0.5 px-2">
                        <span className={`text-text ${tx.voided ? 'line-through' : ''}`}>{tx.description}</span>
                      </td>
                      <td className="py-0.5 px-2 text-right font-mono text-text text-xs">
                        {shares !== 0 ? (shares > 0 ? '+' : '') + formatShares(shares) : ''}
                      </td>
                      <td className="py-0.5 px-2 text-right font-mono text-text-muted text-xs">
                        {pricePerShare !== null && shares !== 0
                          ? `$${(pricePerShare / 100).toFixed(2)}`
                          : ''}
                      </td>
                      <td className={`py-0.5 px-2 text-right font-mono text-xs ${value >= 0 ? 'text-text' : 'text-text-muted'}`}>
                        {formatAmount(Math.abs(value), account.currency)}
                      </td>
                      <td className="py-0.5 px-3 text-right font-mono text-text-muted text-xs">
                        {formatShares(shareBalance)}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="bg-bg-elevated px-6 pb-3 pt-2 border-b border-border">
                          <div className="text-xs">
                            <table className="w-full mb-2">
                              <thead>
                                <tr className="text-text-subtle text-xs border-b border-border">
                                  <th className="text-left pb-1 font-medium">{t('register.columns.account')}</th>
                                  <th className="text-right pb-1 font-medium">{t('register.columns.amount')}</th>
                                  <th className="text-right pb-1 font-medium">{t('register.columns.quantity')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tx.splits.map((s) => {
                                  const acc = ledger.accounts.get(s.accountId);
                                  return (
                                    <tr key={s.splitId} className="border-b border-border">
                                      <td className="py-1 pr-3">
                                        <button
                                          className="text-accent hover:text-accent-hover text-xs"
                                          onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${s.accountId}`); }}
                                        >
                                          {acc?.name ?? s.accountId}
                                        </button>
                                      </td>
                                      <td className="py-1 text-right font-mono text-text">{formatAmount(s.amount, account.currency)}</td>
                                      <td className="py-1 text-right font-mono text-text-muted">
                                        {s.quantity !== s.amount ? formatShares(s.quantity) : ''}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div className="flex gap-4">
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleDelete(tx.id); }}
                                className="text-xs text-negative hover:opacity-80"
                              >
                                {t('common:actions.delete')}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 border-t border-border bg-bg-surface shrink-0 text-xs text-text-muted flex items-center gap-6 flex-wrap">
        <span>
          {t('register.sharesLabel')} <span className="font-mono text-text">{formatShares(sharesHeld)}</span>
        </span>
        <span>
          {t('register.costBasisLabel')} <span className="font-mono text-text">{formatAmount(totalCostBasis, account.currency)}</span>
        </span>
        {marketValue !== null && (
          <span>
            {t('register.mktValueLabel')} <span className="font-mono text-text">{formatAmount(marketValue, account.currency)}</span>
          </span>
        )}
        {unrealized !== null && (
          <span>
            {t('register.gainLabel')}{' '}
            <span className={`font-mono font-medium ${unrealized.amount >= 0 ? 'text-positive' : 'text-negative'}`}>
              {unrealized.amount >= 0 ? '+' : ''}{formatAmount(unrealized.amount, account.currency)}{' '}
              ({unrealized.amount >= 0 ? '+' : ''}{(unrealized.percentage * 100).toFixed(1)}%)
            </span>
          </span>
        )}
        {latestPrice === null && sharesHeld > 0 && (
          <span className="text-text-subtle">{t('register.noPriceHint')}</span>
        )}
        {latestPrice !== null && (
          <span className="ml-auto">
            {t('register.lastPriceLabel')} <span className="font-mono text-text">${(latestPrice.value / 100).toFixed(2)}</span>
            <span className="text-text-subtle ml-1">({latestPrice.date})</span>
          </span>
        )}
      </div>

      {showForm && (
        <InvestmentTransactionForm
          investmentAccountId={account.id}
          worker={worker}
          keyData={keyData}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
